/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { toast } from '../../../../services/toast'
import type { FolderType } from '../../../../services/api/api.foldertypes'
import ValidationSchema from './EditFormValidation'
import { DOC_TYPES_ALLOWED } from './EditFormConstants'
import cloneDeep from 'lodash/cloneDeep'

interface ModalEditProps {
  folderType?: FolderType
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ folderType, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)
  const validationSchema = ValidationSchema()

  const [isEdit, setIsEdit] = useState(!!folderType)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<Promise<any> | null>(null)
  const [loadedFolderType, setLoadedFolderType] = useState<any>({})
  const [initialValues, setInitialValues] = useState<any>(null)

  // Data transformation functions
  const transformFromServer = (item: any) => {
    if (!item || Object.keys(item).length === 0) {
      return {
        id: null,
        name: '',
        description: '',
        documentTypesAllowed: DOC_TYPES_ALLOWED.ALL,
        allowCertifiedCopies: false,
        metadataTemplates: [],
        allowedDocumentTypeTemplates: [],
        allowedDocumentTypeTemplateIds: []
      }
    }

    // Transform metadataTemplates to select options format
    const transformed = { ...item }

    if (transformed.metadataTemplates && Array.isArray(transformed.metadataTemplates)) {
      transformed.metadataTemplates = transformed.metadataTemplates.map((mt: any) => ({
        value: mt.id || mt,
        label: mt.name || mt.toString()
      }))
    } else {
      transformed.metadataTemplates = []
    }

    if (transformed.allowedDocumentTypeTemplates && Array.isArray(transformed.allowedDocumentTypeTemplates)) {
      transformed.allowedDocumentTypeTemplates = transformed.allowedDocumentTypeTemplates.map((dt: any) => ({
        value: dt.id || dt,
        label: dt.name || dt.toString(),
        propertyType: dt.propertyType
      }))
    } else {
      transformed.allowedDocumentTypeTemplates = []
    }

    return transformed
  }

  const transformToServer = (settings: any) => {
    const data = cloneDeep(settings)

    // Extract IDs from select options
    if (data.allowedDocumentTypeTemplates && Array.isArray(data.allowedDocumentTypeTemplates)) {
      data.allowedDocumentTypeTemplateIds = data.allowedDocumentTypeTemplates.map((dt: any) => dt.value || dt.id || dt)
    } else {
      data.allowedDocumentTypeTemplateIds = []
    }

    if (data.metadataTemplates && Array.isArray(data.metadataTemplates)) {
      data.metadataTemplates = data.metadataTemplates.map((mt: any) => mt.value || mt.id || mt)
    } else {
      data.metadataTemplates = []
    }

    return data
  }

  useEffect(() => {
    if (isEdit && folderType?.id) {
      // Set empty initial values immediately so modal can open
      const emptyFolderType = transformFromServer({})
      try {
        const castedValues = validationSchema.cast(emptyFolderType, {
          context: emptyFolderType
        })
        setInitialValues(castedValues)
      } catch (error) {
        console.error('Error casting empty folder type:', error)
        setInitialValues(emptyFolderType)
      }
      // Then load actual data
      loadData()
    } else if (!isEdit) {
      // For add mode, prepare empty initial values immediately
      const emptyFolderType = transformFromServer({})
      try {
        const castedValues = validationSchema.cast(emptyFolderType, {
          context: emptyFolderType
        })
        setInitialValues(castedValues)
      } catch (error) {
        console.error('Error casting empty folder type:', error)
        setInitialValues(emptyFolderType)
      }
    }
  }, [folderType?.id, isEdit])

  const loadData = async () => {
    try {
      const loadPromise = context.api.folderTypes.getById(folderType!.id)
      setLoading(loadPromise)

      const data = await loadPromise
      setLoadedFolderType(data)

      // Transform and cast the loaded data
      const transformedData = transformFromServer(data)
      try {
        const castedValues = validationSchema.cast(transformedData, {
          context: transformedData
        })
        setInitialValues(castedValues)
      } catch (error) {
        console.error('Error casting loaded folder type:', error)
        setInitialValues(transformedData)
      }

      setLoading(null)
    } catch (error) {
      setLoading(null)
    }
  }

  const doSave = async (values: any) => {
    try {
      // Transform form values to API format
      const transformedValues = transformToServer(values)
      let apiCall: Promise<any>

      if (isEdit) {
        apiCall = context.api.folderTypes.update(transformedValues.id, transformedValues)
      } else {
        apiCall = context.api.folderTypes.create(transformedValues)
      }

      setLoading(apiCall)

      await apiCall

      toast.success(translate(isEdit ? 'FOLDER_TYPES_UPDATE_SUCCESS_MSG' : 'FOLDER_TYPES_CREATE_SUCCESS_MSG'))

      // Refresh the table - go to page 1 to see new/updated item
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      } else {
        console.warn('Table context not available for refresh')
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }
      if (isEdit) {
        toast.error(translate('FOLDER_TYPES_UPDATE_FAIL_MSG', { error: errorMsg }))
      } else {
        toast.error(translate('FOLDER_TYPES_CREATE_FAIL_MSG', { error: errorMsg }))
      }
    } finally {
      setLoading(null)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  // Don't render form until initialValues are ready
  if (!initialValues) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'SETTINGS_FOLDER_EDIT' : 'SETTINGS_FOLDER_ADD')}
      size="md"
      dataTestId="edit-folder-type-modal"
    >
      <EditForm onSubmit={doSave} doClose={doClose} isEdit={isEdit} initialValues={initialValues} loading={loading} />
    </Modal>
  )
}

export default ModalEdit
